import { useEffect, useState } from 'react';
import { useShopify } from '../contexts/ShopifyContext';
import { shopifyAuthService } from '../services/shopifyAuthService';

interface ShopifyConnectionState {
  isConnecting: boolean;
  isConnected: boolean;
  error: string | null;
}

/**
 * Hook to handle Shopify connection using session tokens
 * This replaces the old OAuth redirect flow
 */
export const useShopifyConnection = () => {
  const { shop, isEmbedded, getSessionToken } = useShopify();
  const [state, setState] = useState<ShopifyConnectionState>({
    isConnecting: false,
    isConnected: false,
    error: null,
  });

  useEffect(() => {
    const connectShopify = async () => {
      // Only attempt connection if we're embedded and have a shop
      if (!isEmbedded || !shop) {
        return;
      }

      // Attempt to connect using session token
      setState({ isConnecting: true, isConnected: false, error: null });

      try {
        // Get session token from App Bridge
        const sessionToken = await getSessionToken();
        
        if (!sessionToken) {
          throw new Error('Failed to get session token');
        }

        // Exchange session token for access token
        // This is idempotent - safe to call multiple times
        await shopifyAuthService.exchangeToken(sessionToken, shop);

        console.log('[Shopify Connection] Successfully connected');
        setState({ isConnecting: false, isConnected: true, error: null });
      } catch (error: any) {
        console.error('[Shopify Connection] Connection failed:', error);
        setState({
          isConnecting: false,
          isConnected: false,
          error: error.message || 'Connection failed',
        });
      }
    };

    connectShopify();
  }, [shop, isEmbedded, getSessionToken]);

  return state;
};

